import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAlert } from 'react-alert';
import MetaData from '../../layouts/MetaData';
import RoleShell from '../../components/Enrollment/RoleShell';
import { DR_API, errMsg, fmtDate, fmtDateTime, patientLabel, typeLabel } from '../../components/DeviceRequests/deviceRequestApi';
import { useSortableTable, SortableTh, personName } from '../../components/SortableTable';

const REQUEST_SORT_COLUMNS = {
    requestNo: { type: 'string', value: (r) => r?.requestNo },
    patient: { type: 'name', value: (r) => personName(r?.patient) },
    dob: { type: 'date', value: (r) => r?.patient?.DOB },
    emrId: { type: 'string', value: (r) => r?.patient?.emrId },
    organization: { type: 'string', value: (r) => r?.organization?.name },
    provider: { type: 'name', value: (r) => r?.providerName },
    prescribed: { type: 'date', value: (r) => r?.datePrescribed },
    status: { type: 'string', value: (r) => r?.status },
    submitted: { type: 'date', value: (r) => r?.submittedAt },
    approved: { type: 'date', value: (r) => r?.approvedAt },
    batch: { type: 'string', value: (r) => r?.batch?.batchNo },
    delivery: { type: 'string', value: (r) => r?.delivery?.method },
};
const BATCH_SORT_COLUMNS = {
    batchNo: { type: 'string', value: (b) => b?.batchNo },
    date: { type: 'date', value: (b) => b?.date || b?.createdAt },
    organization: { type: 'string', value: (b) => b?.organizationId?.name },
    sentBy: { type: 'name', value: (b) => b?.createdByName },
    count: { type: 'number', value: (b) => b?.count },
};

const todayNY = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

/** Org Manager: today's approved requests -> "Send to Enrollment Coordinator" (one dated batch per organization). */
const DailyEnrollmentRequest = () => {
    const alert = useAlert();
    const [rows, setRows] = useState([]);
    const [batches, setBatches] = useState([]);
    const tableSort = useSortableTable(rows, { id: 'deviceRequests.daily', columns: REQUEST_SORT_COLUMNS, defaultSort: { key: 'patient', dir: 'asc' } });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [confirm, setConfirm] = useState(false);

    const load = useCallback(async () => {
        try {
            const [a, b] = await Promise.all([
                axios.get(DR_API, { params: { status: 'APPROVED', sort: 'submittedAt', order: 'asc', limit: 200 } }),
                axios.get(`${DR_API}/batches`, { params: { date: todayNY() } }),
            ]);
            setRows(a.data.requests || []);
            setBatches(b.data.batches || []);
        } catch (err) { setError(errMsg(err)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const send = async () => {
        setBusy(true);
        setError('');
        try {
            const { data } = await axios.post(`${DR_API}/batches`, {});
            alert.success(`Sent ${data.batches.map((b) => `${b.batchNo} (${b.count})`).join(', ')}`);
            setConfirm(false);
            await load();
        } catch (err) { setError(errMsg(err)); }
        finally { setBusy(false); }
    };

    return (
        <RoleShell title="Daily Enrollment Request">
            <MetaData title="Daily Enrollment Request" />
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Daily Enrollment Request — {todayNY()}</h5>
                <Link to="/device-requests" className="btn btn-link btn-sm">Review queue</Link>
            </div>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <table className="table table-sm table-striped" data-testid="dr-daily">
                <thead><tr>
                    <SortableTh sortKey="requestNo" sort={tableSort}>Request</SortableTh><SortableTh sortKey="patient" sort={tableSort}>Patient</SortableTh>
                    <SortableTh sortKey="organization" sort={tableSort}>Organization</SortableTh><th>Device(s)</th>
                    <SortableTh sortKey="provider" sort={tableSort}>Provider</SortableTh><SortableTh sortKey="prescribed" sort={tableSort}>Prescribed</SortableTh>
                    <SortableTh sortKey="delivery" sort={tableSort}>Delivery</SortableTh><SortableTh sortKey="approved" sort={tableSort}>Approved</SortableTh>
                </tr></thead>
                <tbody>
                    {tableSort.rows.map((r) => (
                        <tr key={r._id}>
                            <td><Link to={`/device-requests/${r._id}`}>{r.requestNo}</Link></td><td>{patientLabel(r.patient)}</td><td>{r.organization?.name}</td>
                            <td>{(r.deviceTypes || []).map(typeLabel).join(', ')}</td><td>{r.providerName}</td><td>{fmtDate(r.datePrescribed)}</td>
                            <td>{r.delivery?.method === 'ship' ? 'Ship' : 'In clinic'}</td>
                            <td>{fmtDateTime(r.approvedAt)}{r.approvedDate && r.approvedDate !== todayNY() && <span className="badge badge-warning ml-1">from {r.approvedDate}</span>}</td>
                        </tr>
                    ))}
                    {!rows.length && <tr><td colSpan={8} className="text-muted">No enrollment requests today</td></tr>}
                </tbody>
            </table>
            {rows.length > 0 && !confirm && <button type="button" className="btn btn-primary" onClick={() => setConfirm(true)}>Send to Enrollment Coordinator ({rows.length})</button>}
            {confirm && (
                <div className="alert alert-info">
                    Send {rows.length} approved request{rows.length === 1 ? '' : 's'} to the Enrollment Coordinator? A dated batch is created per organization and the coordinators are emailed (no patient details in the email).
                    <div className="mt-2">
                        <button type="button" className="btn btn-primary btn-sm mr-2" disabled={busy} onClick={send}>{busy ? 'Sending…' : 'Confirm send'}</button>
                        <button type="button" className="btn btn-link btn-sm" onClick={() => setConfirm(false)}>Cancel</button>
                    </div>
                </div>
            )}
            <h6 className="mt-4">Batches sent today</h6>
            <ul data-testid="dr-today-batches">
                {batches.map((b) => <li key={b._id}><Link to={`/device-requests/batches/${b._id}`}>{b.batchNo}</Link> — {b.organizationId?.name} · {b.count} request(s) · {b.createdByName} · {fmtDateTime(b.createdAt)}</li>)}
                {!batches.length && <li className="text-muted">None yet.</li>}
            </ul>
        </RoleShell>
    );
};

export default DailyEnrollmentRequest;
